class BulkBundle {
  constructor() {
    this.bulkForm = document.querySelector("[data-bulk-form]");
    this.currentVariant = null;
    this.bundleData = window.bundleConfig?.bundleData || [];
    
    if (!this.bulkForm || !window.bundleConfig) {
      return;
    }
    
    this.setupEventListeners();
    this.initialize();
  }

  setupEventListeners() {
    const productForm = document.querySelector('form[is="product-form"]');
    if (productForm) {
      productForm.addEventListener(
        "variant:change",
        this.onVariantChange.bind(this)
      );
    }

    document.addEventListener("click", (event) => {
      if (event.target.classList.contains("bulk-add-to-cart-btn")) {
        event.preventDefault();
        const variantId = event.target.dataset.variantId;
        this.addBulkToCart(variantId);
      }
    });
  }

  onVariantChange(event) {
    this.currentVariant = event.detail.variant;
    this.updateBulkBundle();
  }

  initialize() {
    this.getCurrentVariant().then(variant => {
      if (variant) {
        this.currentVariant = variant;
        this.updateBulkBundle();
      }
    });
  }

  async getCurrentVariant() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get("variant");
      
      const productHandle = window.bundleConfig?.currentProductHandle;
      if (!productHandle) {
        return null;
      }

      const response = await fetch(`${window.Shopify.routes.root}products/${productHandle}.js`);
      const product = await response.json();
      
      if (variantId) {
        const variant = product.variants.find(v => v.id == variantId);
        if (variant) return variant;
      }
      
      return product.variants.find(v => v.available) || product.variants[0];
    } catch (error) {
      return null;
    }
  }

  updateBulkBundle() {
    if (!this.currentVariant) return;
    
    this.clearBulkContent();
    
    const linkedBundle = this.findLinkedBundle();
    
    if (linkedBundle) {
      this.fetchAndRenderBundleProduct(linkedBundle);
    } else {
      this.showNoBulkMessage();
    }
  }

  clearBulkContent() {
    const existingWrapper = this.bulkForm.querySelector(".bulk-bundle-wrapper");
    if (existingWrapper) existingWrapper.remove();
  }

  findLinkedBundle() {
    if (!this.bundleData || this.bundleData.length === 0) {
      return null;
    }

    const match = this.bundleData.find(item => {
      const variantId = Number(item.vId);
      return variantId === this.currentVariant.id;
    });

    if (match) {
      return {
        variantId: match.bundleId,
        productId: match.bundleProductId,
        handle: match.bundleHandle
      };
    }

    return null;
  }

  async fetchAndRenderBundleProduct(linkedBundle) {
    try {
      if (linkedBundle.handle && linkedBundle.handle !== 'null') {
        const response = await fetch(`${window.Shopify.routes.root}products/${linkedBundle.handle}.js`);
        
        if (response.ok) {
          const product = await response.json();
          const variant = product.variants.find(v => v.id === linkedBundle.variantId);
          
          if (variant) {
            this.renderBulkProductCard(variant, product);
            return;
          }
        }
      }

      await this.fetchFromCollection(linkedBundle);

    } catch (error) {
      this.showNoBulkMessage();
    }
  }

  async fetchFromCollection(linkedBundle) {
    try {
      const collectionHandle = window.bundleConfig?.promoCollectionHandle;
      if (!collectionHandle) {
        this.showNoBulkMessage();
        return;
      }

      const response = await fetch(`${window.Shopify.routes.root}collections/${collectionHandle}/products.json?limit=250`);
      const data = await response.json();

      for (const product of data.products) {
        const variant = product.variants.find(v => v.id === linkedBundle.variantId);
        if (variant) {
          this.renderBulkProductCard(variant, product);
          return;
        }
      }

      this.showNoBulkMessage();
    } catch (error) {
      this.showNoBulkMessage();
    }
  }

  renderBulkProductCard(variant, product) {
    const bulkCard = this.createBulkProductCard(variant, product);

    const bulkWrapper = `
      <div class="bulk-bundle-wrapper">
        <div class="bulk-bundle-grid">
          ${bulkCard}
        </div>
      </div>
    `;

    this.bulkForm.insertAdjacentHTML("beforeend", bulkWrapper);
  }

  getProductImage(product, variant = null) {
    if (variant && variant.featured_image) {
      const variantImage = variant.featured_image.src || variant.featured_image;
      if (typeof variantImage === "string") return variantImage;
    }

    if (product.featured_image) {
      if (typeof product.featured_image === "string")
        return product.featured_image;
    }

    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (typeof firstImage === "string") return firstImage;
      if (firstImage && firstImage.src && typeof firstImage.src === "string")
        return firstImage.src;
    }

    return window.bundleConfig?.placeholderImageUrl || `${window.Shopify.routes.root}assets/placeholder.png`;
  }

  createBulkProductCard(variant, product) {
    const currentProductTitle = window.bundleConfig?.currentProductTitle || "Product";
    
    const currentVariantTitle = this.currentVariant?.title && this.currentVariant.title !== "Default Title" 
                               ? this.currentVariant.title 
                               : "";
    
    const price = (variant.price / 100).toFixed(2);
    const compareAtPrice = variant.compare_at_price ? (variant.compare_at_price / 100).toFixed(2) : null;
    let imageUrl = this.getProductImage(product, variant);
    
    if (imageUrl.startsWith("//")) {
      imageUrl = `https:${imageUrl}`;
    }

    const bundleItem = this.bundleData.find(item => item.bundleId === variant.id);
    const bulkQuantity = bundleItem?.qty || '10';
    const placeholderUrl = window.bundleConfig?.placeholderImageUrl || `${window.Shopify.routes.root}assets/placeholder.png`;

    let priceDisplay = `£${price}`;
    let badgeText = '';
    
    if (compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price)) {
      const savings = (parseFloat(compareAtPrice) - parseFloat(price)).toFixed(2);
      priceDisplay = `<span class="price--strikethrough">£${compareAtPrice}</span> £${price}`;
      badgeText = `Save £${savings}`;
    }

    return `
      <div class="bundle-item bundle-item--filled bulk-bundle-card">
        <div class="bulk-product-card">
          <div class="bundle-product-card__media bulk-product-card__media">
            <img src="${imageUrl}" alt="${product.title}" width="150" 
                 onerror="this.src='${placeholderUrl}'">
            ${badgeText ? `<div class="bundle-product-card__edit">
              <span class="bulk-bundle-badge">${badgeText}</span>
            </div>` : ''}
          </div>
          <div class="bundle-product-card__details bulk-product-card__details">
            <h4>${currentProductTitle} (Box of ${bulkQuantity})</h4>
            ${currentVariantTitle ? `<h5>${currentVariantTitle}</h5>` : ""}
            <p>${priceDisplay}</p>
            <button type="button" class="button button--secondary bulk-add-to-cart-btn" 
                    data-variant-id="${variant.id}" 
                    is="hover-button">
              Add Box to Cart
            </button>
          </div>
        </div>
      </div>
    `;
  }

  showNoBulkMessage() {
  }

  async addBulkToCart(variantId) {
    const data = {
      items: [{
        id: parseInt(variantId),
        quantity: 1,
      }],
    };

    if (
      document.body.classList.contains("template-cart") ||
      theme.settings.cartType === "page"
    ) {
      theme.utils.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...data,
        },
      });
      return;
    }

    const bulkButton = document.querySelector(".bulk-add-to-cart-btn");
    if (bulkButton) {
      bulkButton.setAttribute("aria-disabled", "true");
      bulkButton.setAttribute("aria-busy", "true");
      bulkButton.innerText = "Adding to cart...";
    }

    try {
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const body = JSON.stringify({
        ...data,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${theme.routes.cart_add_url}`, {
        ...theme.utils.fetchConfig("javascript"),
        body,
      });

      const parsedState = await response.json();

      if (parsedState.status) {
        return;
      }

      const cartJson = await (
        await fetch(theme.routes.cart_url, {
          ...theme.utils.fetchConfig("json", "GET"),
        })
      ).json();
      cartJson["sections"] = parsedState["sections"];

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: "bulk-bundle",
        cart: cartJson,
      });

      document.dispatchEvent(
        new CustomEvent("ajaxProduct:added", {
          detail: {
            product: parsedState,
          },
        })
      );

      const cartDrawer = document.querySelector("cart-drawer");
      cartDrawer?.show();

    } catch (error) {
    } finally {
      if (bulkButton) {
        bulkButton.removeAttribute("aria-disabled");
        bulkButton.removeAttribute("aria-busy");
        bulkButton.innerText = "Add Box to Cart";
      }

      document.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: { open: true },
        })
      );
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  setTimeout(() => {
    const bulkBundle = new BulkBundle();
    window.bulkBundleInstance = bulkBundle;
  }, 100);
});
